import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { vacationApi, remoteApi, personalDaysApi } from '../utils/api';
import { Palmtree, Laptop, Loader, RefreshCw, AlertCircle, CalendarDays, Clock, CheckCircle, Coffee } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const STATUS_CLASSES = {
  pending: 'status-pending',
  approved: 'status-approved',
  rejected: 'status-rejected',
  cancelled: 'status-cancelled',
  cancellation_requested: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300',
};
const STATUS_LABELS = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  cancelled: 'Cancelado',
  cancellation_requested: 'Cancelación pendiente',
};

const periodLabels = { morning: 'Mañana', afternoon: 'Tarde', full_day: 'Día completo' };

const StatusBadge = ({ status }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CLASSES[status] || 'bg-muted text-muted-foreground'}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

export default function MyRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vacations, setVacations] = useState([]);
  const [remotes, setRemotes] = useState([]);
  const [personalDays, setPersonalDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [error, setError] = useState('');

  // Cancel dialog
  const [cancelDialog, setCancelDialog] = useState(null); // { type, id, label }
  const [cancelReason, setCancelReason] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vacRes, remRes, pdRes] = await Promise.all([
        vacationApi.list(),
        remoteApi.list(),
        personalDaysApi.list().catch(() => ({ data: [] })),
      ]);
      setVacations(vacRes.data);
      setRemotes(remRes.data);
      setPersonalDays(pdRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCancelDialog = (type, id, label) => {
    setCancelDialog({ type, id, label });
    setCancelReason('');
    setError('');
  };

  const confirmCancel = async () => {
    if (!cancelDialog) return;
    const { type, id } = cancelDialog;
    setProcessing(p => ({ ...p, [id]: true }));
    setError('');
    try {
      if (type === 'vacation') await vacationApi.cancel(id, cancelReason);
      else await remoteApi.cancel(id, cancelReason);
      setCancelDialog(null);
      setCancelReason('');
      setSuccessMsg('Solicitud de cancelación enviada a tu responsable para aprobación.');
      setTimeout(() => setSuccessMsg(''), 4000);
      await loadData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al solicitar la cancelación');
    } finally {
      setProcessing(p => ({ ...p, [id]: false }));
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader className="h-6 w-6 animate-spin text-brand" />
    </div>
  );

  const canCancel = (status) => status === 'pending' || status === 'approved';
  const canCancelVacation = (req) => {
    if (!canCancel(req.status)) return false;
    // Must cancel at least 1 day before start
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(req.start_date + 'T12:00');
    return startDate > today;
  };

  const VacationRow = ({ req }) => (
    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`vacation-row-${req.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="h-9 w-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
          <Palmtree className="h-4 w-4 text-green-600 dark:text-green-400" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium">{req.start_date} → {req.end_date}</div>
          <div className="text-xs text-muted-foreground">
            {req.working_days} días laborables
            {req.carryover_days_used > 0 && ` (${req.carryover_days_used}d arrastrado)`}
          </div>
          {req.notes && <div className="text-xs text-muted-foreground italic truncate">"{req.notes}"</div>}
          {req.approver_comment && (
            <div className="text-xs text-muted-foreground mt-0.5">
              Comentario: <span className="italic">{req.approver_comment}</span>
            </div>
          )}
          {req.cancel_reason && (
            <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
              Motivo cancelación: <span className="italic">{req.cancel_reason}</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        <StatusBadge status={req.status} />
        {canCancelVacation(req) && (
          <Button
            size="sm" variant="ghost"
            onClick={() => openCancelDialog('vacation', req.id, `${req.start_date} - ${req.end_date}`)}
            disabled={!!processing[req.id]}
            data-testid={`cancel-vacation-${req.id}`}
            className="h-7 px-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 text-xs"
          >
            {processing[req.id] ? <Loader className="h-3.5 w-3.5 animate-spin" /> : 'Cancelar'}
          </Button>
        )}
      </div>
    </div>
  );

  const RemoteRow = ({ req }) => (
    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`remote-row-${req.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="h-9 w-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
          <Laptop className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium">{req.date}</div>
          <div className="text-xs text-muted-foreground">
            {periodLabels[req.period_type] || req.period_type}
            {req.location && ` · ${req.location}`}
            {req.from_annual_plan && <span className="ml-1 text-blue-500">(Plan anual)</span>}
          </div>
          {req.approver_comment && (
            <div className="text-xs text-muted-foreground mt-0.5 italic">{req.approver_comment}</div>
          )}
          {req.cancel_reason && (
            <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
              Motivo cancelación: <span className="italic">{req.cancel_reason}</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        <StatusBadge status={req.status} />
        {canCancel(req.status) && (
          <Button
            size="sm" variant="ghost"
            onClick={() => openCancelDialog('remote', req.id, req.date)}
            disabled={!!processing[req.id]}
            data-testid={`cancel-remote-${req.id}`}
            className="h-7 px-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 text-xs"
          >
            {processing[req.id] ? <Loader className="h-3.5 w-3.5 animate-spin" /> : 'Cancelar'}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-heading">Mis Solicitudes</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Historial de tus solicitudes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => navigate('/remote-work/plan')}
            data-testid="btn-go-annual-plan"
            className="gap-1.5 text-xs"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Plan anual
          </Button>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="btn-refresh-requests">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {successMsg && (
        <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-sm text-green-700 dark:text-green-300" data-testid="success-msg">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />{successMsg}
        </div>
      )}

      {error && !cancelDialog && (
        <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />{error}
        </div>
      )}

      {/* Cancellation pending notice */}
      {[...vacations, ...remotes].some(r => r.status === 'cancellation_requested') && (
        <div className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 text-sm text-orange-700 dark:text-orange-300" data-testid="cancellation-pending-notice">
          <Clock className="h-4 w-4 flex-shrink-0" />
          Tienes solicitudes de cancelación pendientes de aprobación por tu responsable.
        </div>
      )}

      <Tabs defaultValue="vacations" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="vacations" data-testid="tab-vacations" className="gap-2">
            <Palmtree className="h-3.5 w-3.5" />Vacaciones ({vacations.length})
          </TabsTrigger>
          <TabsTrigger value="remote" data-testid="tab-remote" className="gap-2">
            <Laptop className="h-3.5 w-3.5" />Teletrabajo ({remotes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vacations">
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm space-y-2">
            {vacations.length === 0
              ? <div className="py-8 text-center text-muted-foreground"><Palmtree className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No tienes solicitudes de vacaciones</p></div>
              : vacations.map(v => <VacationRow key={v.id} req={v} />)
            }
          </div>
        </TabsContent>

        <TabsContent value="remote">
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm space-y-2">
            {remotes.length === 0
              ? <div className="py-8 text-center text-muted-foreground"><Laptop className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No tienes solicitudes de teletrabajo</p></div>
              : remotes.map(r => <RemoteRow key={r.id} req={r} />)
            }
          </div>
        </TabsContent>

        <TabsContent value="personal">
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm space-y-2">
            {personalDays.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <Coffee className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No tienes solicitudes de asuntos propios</p>
              </div>
            ) : personalDays.map(pd => (
              <div key={pd.id} className="flex items-center justify-between p-3 rounded-lg bg-amber-50/40 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/40">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <Coffee className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{pd.date}</div>
                    <div className="text-xs text-muted-foreground">
                      {pd.convenio_name || 'Asunto propio'}
                      {pd.notes && ` · ${pd.notes}`}
                    </div>
                    {pd.approver_comment && (
                      <div className="text-xs text-muted-foreground mt-0.5 italic">{pd.approver_comment}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <StatusBadge status={pd.status} />
                  {(pd.status === 'pending' || pd.status === 'approved') && (
                    <Button
                      size="sm" variant="ghost"
                      onClick={async () => {
                        try {
                          await personalDaysApi.cancel(pd.id);
                          setSuccessMsg('Solicitud cancelada');
                          loadData();
                        } catch (e) { setError(e.response?.data?.detail || 'Error'); }
                      }}
                      data-testid={`cancel-personal-${pd.id}`}
                      className="h-7 px-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 text-xs"
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Cancel dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => { setCancelDialog(null); setCancelReason(''); setError(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Solicitar cancelación</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {cancelDialog && (
              <p className="text-sm text-muted-foreground">
                Vas a solicitar la cancelación de: <strong>{cancelDialog.label}</strong>.
              </p>
            )}
            <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 text-xs text-orange-700 dark:text-orange-300 flex items-start gap-2">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              La cancelación requiere aprobación de tu responsable. Hasta que sea aprobada, la solicitud permanecerá activa.
            </div>
            <Label className="text-sm font-medium">Motivo (opcional)</Label>
            <Textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Explica el motivo de la cancelación..."
              rows={3}
              className="resize-none"
              data-testid="cancel-reason-input"
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(null)}>Volver</Button>
            <Button
              onClick={confirmCancel}
              disabled={cancelDialog && !!processing[cancelDialog.id]}
              data-testid="confirm-cancel-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
            >
              {cancelDialog && processing[cancelDialog.id]
                ? <><Loader className="h-3.5 w-3.5 animate-spin" />Enviando...</>
                : 'Solicitar cancelación'
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
